/**
 * @fileoverview Section "Charts" — primitives Recharts du design system GNPC.
 *
 * Les dashboards utilisent `ChartContainer`, `ChartTooltip`,
 * `ChartTooltipContent`, `ChartLegend` et `ChartLegendContent` pour garantir
 * les couleurs de séries, tooltips, légendes, radius et absence d'ombre. Le
 * graphe métier (`BarChart`, `PieChart`, etc.) reste fourni par Recharts.
 */

import { useMemo } from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, Pie, PieChart, XAxis, YAxis } from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@gnpc/ui';
import { DsPreview } from '../components/DsPreview.tsx';

const CHART_DATA = [
  { date: '2026-07-08', payments: 3580, fines: 5812, failed: 61 },
  { date: '2026-07-09', payments: 3712, fines: 6074, failed: 58 },
  { date: '2026-07-10', payments: 3926, fines: 6551, failed: 63 },
  { date: '2026-07-11', payments: 3488, fines: 5689, failed: 54 },
  { date: '2026-07-12', payments: 3654, fines: 5941, failed: 57 },
  { date: '2026-07-13', payments: 3810, fines: 6237, failed: 49 },
  { date: '2026-07-14', payments: 4127, fines: 6810, failed: 66 },
];

const chartConfig = {
  payments: {
    label: 'Paiements',
  },
  successful: {
    label: 'Succès',
    color: 'var(--chart-2)',
  },
  failed: {
    label: 'Échecs',
    color: 'var(--chart-5)',
  },
  fines: {
    label: 'Amendes',
    color: 'var(--chart-3)',
  },
} satisfies ChartConfig;

const PAYMENT_METHOD_DATA = [
  { method: 'orange', payments: 10151, fill: 'var(--color-orange)' },
  { method: 'moov', payments: 6022, fill: 'var(--color-moov)' },
  { method: 'mtn', payments: 4707, fill: 'var(--color-mtn)' },
  { method: 'wave', payments: 5417, fill: 'var(--color-wave)' },
];

const paymentMethodConfig = {
  payments: { label: 'Paiements' },
  orange: { label: 'Orange Money', color: 'var(--chart-1)' },
  moov: { label: 'Moov Money', color: 'var(--chart-4)' },
  mtn: { label: 'MTN Money', color: 'var(--warning)' },
  wave: { label: 'Wave', color: 'var(--chart-2)' },
} satisfies ChartConfig;

type ChartKey = 'payments' | 'fines' | 'failed';
const CHART_KEYS: ChartKey[] = ['payments', 'fines', 'failed'];

export function DsSectionCharts(): JSX.Element {
  const totals = useMemo(
    () => ({
      payments: CHART_DATA.reduce((total, item) => total + item.payments, 0),
      fines: CHART_DATA.reduce((total, item) => total + item.fines, 0),
      failed: CHART_DATA.reduce((total, item) => total + item.failed, 0),
    }),
    [],
  );
  const stackedChartData = useMemo(
    () =>
      CHART_DATA.map((item) => ({
        ...item,
        successful: Math.max(item.payments - item.failed, 0),
      })),
    [],
  );

  return (
    <div className="space-y-5">
      <p className="text-muted-foreground text-sm">
        Les graphiques utilisent Recharts pour la logique de rendu et les primitives GNPC pour les
        couleurs, tooltips et dimensions. Définir les séries dans un `ChartConfig` et utiliser
        `var(--color-nomSerie)` dans les marks Recharts.
      </p>

      <DsPreview label="Graphique combiné — paiements, amendes et échecs">
        <Card className="w-full py-0">
          <CardHeader className="p-0! flex flex-col items-stretch border-b sm:flex-row">
            <div className="flex flex-1 flex-col justify-center gap-1 px-5 pb-3 pt-4 sm:py-0">
              <CardTitle>Évolution des paiements et des amendes</CardTitle>
              <CardDescription>Volumes quotidiens et échecs sur 7 jours.</CardDescription>
            </div>
            <div className="flex">
              {CHART_KEYS.map((key) => (
                <div
                  key={key}
                  className="relative z-10 flex flex-1 flex-col justify-center gap-1 border-t px-5 py-4 text-left sm:min-w-36 sm:border-l sm:border-t-0"
                >
                  <span className="text-muted-foreground text-xs">{chartConfig[key].label}</span>
                  <span className="text-quipux-navy-950 text-lg font-semibold tabular-nums leading-none sm:text-2xl">
                    {totals[key].toLocaleString('fr-FR')}
                  </span>
                </div>
              ))}
            </div>
          </CardHeader>
          <CardContent className="px-2 pb-5 pt-3 sm:px-6 sm:pb-6 sm:pt-4">
            <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
              <ComposedChart
                accessibilityLayer
                data={stackedChartData}
                margin={{ left: 12, right: 12 }}
              >
                <CartesianGrid vertical={false} />
                <YAxis hide />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                  tickFormatter={(value: string) =>
                    new Date(`${value}T00:00:00`).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'short',
                    })
                  }
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      className="w-44"
                      labelFormatter={(value) =>
                        new Date(`${value}T00:00:00`).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        })
                      }
                    />
                  }
                />
                <Bar
                  dataKey="successful"
                  stackId="payments"
                  fill="var(--color-successful)"
                  radius={[0, 0, 4, 4]}
                  maxBarSize={72}
                />
                <Bar
                  stackId="payments"
                  dataKey="failed"
                  fill="var(--color-failed)"
                  radius={[4, 4, 0, 0]}
                  minPointSize={4}
                  maxBarSize={72}
                />
                <Line
                  type="monotone"
                  dataKey="fines"
                  stroke="var(--color-fines)"
                  strokeWidth={3}
                  dot={{ fill: 'var(--color-fines)', r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </DsPreview>

      <DsPreview label="Donut avec légende — répartition par moyen de paiement">
        <Card className="flex w-full max-w-md flex-col py-0">
          <CardHeader className="gap-1 px-5 pb-2 pt-4">
            <CardTitle>Répartition par moyen de paiement</CardTitle>
            <CardDescription>Exemple sur une période sélectionnée.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-1 items-center px-3 pb-4 pt-0">
            <ChartContainer
              config={paymentMethodConfig}
              className="mx-auto aspect-square max-h-[300px] w-full"
            >
              <PieChart accessibilityLayer>
                <ChartTooltip content={<ChartTooltipContent hideLabel nameKey="method" />} />
                <Pie
                  data={PAYMENT_METHOD_DATA}
                  dataKey="payments"
                  nameKey="method"
                  innerRadius={54}
                  outerRadius={90}
                  paddingAngle={2}
                  strokeWidth={0}
                />
                <ChartLegend
                  content={<ChartLegendContent nameKey="method" />}
                  className="flex-wrap gap-x-4 gap-y-2 pt-3"
                />
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </DsPreview>
    </div>
  );
}
